import api from '../../services/api';

// Kompozit rapor payload'larını sayfa görünümüne (KPI + bölüm tabloları)
// çevirir. Sunucudaki export eşleyicisinin ekran karşılığıdır.

const fmtTry = (v) => `${Number(v ?? 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺`;
const fmtMonth = (iso, lang) => new Date(iso).toLocaleDateString(lang, { month: 'long', year: 'numeric' });
const fmtDate = (iso, lang) => new Date(iso).toLocaleDateString(lang);
const orDash = (v) => (v == null || v === '' ? '—' : v);
const pct = (v) => (v == null ? '—' : `%${v}`);

export async function getExpenseCategories() {
  const res = await api.get('/v1/expense-categories?pageSize=200');
  return res.data?.data?.data ?? [];
}

export default function buildSections(slug, data, t, lang) {
  switch (slug) {
    case 'department': {
      const view = {
        kpis: [
          { label: t('expenseReports.kpi.total'), value: fmtTry(data.rows.reduce((s, r) => s + r.total, 0)) },
          { label: t('expenseReports.kpi.count'), value: data.rows.reduce((s, r) => s + r.count, 0) },
          { label: t('expenseReports.kpi.violations'), value: data.rows.reduce((s, r) => s + r.violationCount, 0) },
        ],
        sections: [{
          title: t('expenseReports.sections.departments'),
          columns: [t('expenseReports.cols.department'), t('expenseReports.cols.total'), t('expenseReports.cols.count'),
            t('expenseReports.cols.headcount'), t('expenseReports.cols.perEmployee'), t('expenseReports.cols.compareTotal'), t('expenseReports.cols.violations')],
          rows: data.rows.map((r) => [orDash(r.department), fmtTry(r.total), r.count, r.employeeCount, fmtTry(r.perEmployee), fmtTry(r.compareTotal), r.violationCount]),
        }],
        footnotes: [],
      };
      if (data.detail) {
        view.sections.push({
          title: t('expenseReports.sections.categoryBreakdown'),
          columns: [t('expenseReports.cols.category'), t('expenseReports.cols.total'), t('expenseReports.cols.count'), t('expenseReports.cols.share')],
          rows: data.detail.categories.map((c) => [c.categoryName, fmtTry(c.total), c.count, pct(c.sharePct)]),
        });
        view.sections.push({
          title: t('expenseReports.sections.monthlySeries'),
          columns: [t('expenseReports.cols.month'), t('expenseReports.cols.total'), t('expenseReports.cols.count')],
          rows: data.detail.monthlySeries.map((m) => [fmtMonth(m.monthStart, lang), fmtTry(m.total), m.count]),
        });
        view.sections.push({
          title: t('expenseReports.sections.locationBreakdown'),
          columns: [t('expenseReports.cols.location'), t('expenseReports.cols.total'), t('expenseReports.cols.count')],
          rows: data.detail.locations.map((l) => [orDash(l.location), fmtTry(l.total), l.count]),
        });
      }
      if (data.nonTryCount > 0) view.footnotes.push(t('expenseDashboard.fxExcluded', { count: data.nonTryCount }));
      if (data.violationDataPartial) view.footnotes.push(t('expenseReports.notes.violationPartial'));
      return view;
    }

    case 'category': {
      const view = {
        kpis: [
          { label: t('expenseReports.kpi.total'), value: fmtTry(data.rows.reduce((s, r) => s + r.total, 0)) },
          { label: t('expenseReports.kpi.categories'), value: data.rows.length },
        ],
        sections: [{
          title: t('expenseReports.sections.categories'),
          columns: [t('expenseReports.cols.category'), t('expenseReports.cols.total'), t('expenseReports.cols.count'),
            t('expenseReports.cols.avgPerTxn'), t('expenseReports.cols.topSpender'), t('expenseReports.cols.growth')],
          rows: data.rows.map((r) => [r.categoryName, fmtTry(r.total), r.count, fmtTry(r.avgPerTransaction), orDash(r.topSpenderName), pct(r.growthPct)]),
        }, {
          title: t('expenseReports.sections.deptCategoryMatrix'),
          columns: [t('expenseReports.cols.department'), t('expenseReports.cols.category'), t('expenseReports.cols.total')],
          rows: data.matrix.map((m) => [orDash(m.department), m.categoryName, fmtTry(m.total)]),
        }, {
          title: t('expenseReports.sections.monthlySeries'),
          columns: [t('expenseReports.cols.month'), t('expenseReports.cols.category'), t('expenseReports.cols.total')],
          rows: data.monthlySeries.map((m) => [fmtMonth(m.monthStart, lang), m.categoryName, fmtTry(m.total)]),
        }, {
          title: t('expenseReports.sections.locationCategoryMatrix'),
          columns: [t('expenseReports.cols.location'), t('expenseReports.cols.category'), t('expenseReports.cols.total')],
          rows: data.locationMatrix.map((m) => [orDash(m.location), m.categoryName, fmtTry(m.total)]),
        }],
        footnotes: [],
      };
      if (data.nonTryCount > 0) view.footnotes.push(t('expenseDashboard.fxExcluded', { count: data.nonTryCount }));
      if (data.insufficientHistory) view.footnotes.push(t('expenseReports.notes.insufficientHistory'));
      return view;
    }

    case 'employee': {
      const view = {
        kpis: [
          { label: t('expenseReports.kpi.total'), value: fmtTry(data.rows.reduce((s, r) => s + r.total, 0)) },
          { label: t('expenseReports.kpi.employees'), value: data.rows.length },
        ],
        sections: [{
          title: t('expenseReports.sections.employees'),
          columns: [t('expenseReports.cols.employee'), t('expenseReports.cols.department'), t('expenseReports.cols.total'),
            t('expenseReports.cols.count'), t('expenseReports.cols.avgPerTxn'), t('expenseReports.cols.topCategory'),
            t('expenseReports.cols.compareTotal'), t('expenseReports.cols.violations')],
          rows: data.rows.map((r) => [r.fullName, orDash(r.department), fmtTry(r.total), r.count,
            fmtTry(r.avgPerTransaction), orDash(r.topCategoryName), fmtTry(r.compareTotal), r.violationCount]),
        }],
        footnotes: [],
      };
      if (data.detail) {
        view.sections.push({
          title: t('expenseReports.sections.weekDistribution'),
          columns: [t('expenseReports.cols.week'), t('expenseReports.cols.total'), t('expenseReports.cols.count')],
          rows: data.detail.weekDistribution.map((w) => [t('expenseReports.cols.weekOf', { week: w.weekOfMonth }), fmtTry(w.total), w.count]),
        });
        view.sections.push({
          title: t('expenseReports.sections.categoryVsDept'),
          columns: [t('expenseReports.cols.category'), t('expenseReports.cols.employeeTotal'), t('expenseReports.cols.deptAvg')],
          rows: data.detail.categories.map((c) => [c.categoryName, fmtTry(c.total), fmtTry(c.deptAvgPerEmployee)]),
        });
        view.sections.push({
          title: t('expenseReports.sections.approvalHistory'),
          columns: [t('expenseReports.cols.date'), t('expenseReports.cols.action'), t('expenseReports.cols.performedBy'), t('expenseReports.cols.note')],
          rows: data.detail.approvalHistory.map((h) => [fmtDate(h.date, lang),
            t(`expenseReports.actions.${h.actionType}`, { defaultValue: h.actionType }), orDash(h.performedBy), orDash(h.notes)]),
        });
      }
      if (data.nonTryCount > 0) view.footnotes.push(t('expenseDashboard.fxExcluded', { count: data.nonTryCount }));
      return view;
    }

    case 'approval-process': {
      const durCols = [t('expenseReports.cols.decidedCount'), t('expenseReports.cols.avgDays'), t('expenseReports.cols.maxDays')];
      const view = {
        kpis: [
          { label: t('expenseReports.kpi.slaCompliant'), value: data.slaCompliantCount },
          { label: t('expenseReports.kpi.slaBreached'), value: data.slaBreachedCount },
          { label: t('expenseReports.kpi.slaTarget'), value: t('expenseReports.kpi.slaDays', { days: data.slaBreachDays }) },
        ],
        sections: [{
          title: t('expenseReports.sections.durationByDept'),
          columns: [t('expenseReports.cols.department'), ...durCols],
          rows: data.byDepartment.map((r) => [r.key, r.decidedCount, r.avgDays, r.maxDays]),
        }, {
          title: t('expenseReports.sections.durationByApprover'),
          columns: [t('expenseReports.cols.approver'), ...durCols],
          rows: data.byApprover.map((r) => [r.key, r.decidedCount, r.avgDays, r.maxDays]),
        }, {
          title: t('expenseReports.sections.durationByAmount'),
          columns: [t('expenseReports.cols.amountBand'), ...durCols],
          rows: data.byAmountBand.map((r) => [r.key, r.decidedCount, r.avgDays, r.maxDays]),
        }, {
          title: t('expenseReports.sections.rejectionsByCategory'),
          columns: [t('expenseReports.cols.category'), t('expenseReports.cols.rejected'), t('expenseReports.cols.decidedCount'), t('expenseReports.cols.rejectionRate')],
          rows: data.rejectionsByCategory.map((r) => [r.categoryName, r.rejectedCount, r.totalCount, pct(r.rejectionRatePct)]),
        }, {
          title: t('expenseReports.sections.topRejectionReasons'),
          columns: [t('expenseReports.cols.reason'), t('expenseReports.cols.count')],
          rows: data.topRejectionReasons.map((r) => [r.reason, r.count]),
        }],
        footnotes: [],
      };
      if (data.historicalApproximate) view.footnotes.push(t('expenseReports.notes.historicalApproximate'));
      return view;
    }

    case 'behavior': {
      const dowNames = [
        t('expenseDashboard.heatmap.mon'), t('expenseDashboard.heatmap.tue'), t('expenseDashboard.heatmap.wed'),
        t('expenseDashboard.heatmap.thu'), t('expenseDashboard.heatmap.fri'), t('expenseDashboard.heatmap.sat'), t('expenseDashboard.heatmap.sun'),
      ];
      const view = {
        kpis: [],
        sections: [{
          title: t('expenseReports.sections.dayDistribution'),
          columns: [t('expenseReports.cols.day'), t('expenseReports.cols.count'), t('expenseReports.cols.total')],
          rows: data.dayDistribution.map((d) => [dowNames[Math.min(6, Math.max(0, d.dow - 1))], d.count, fmtTry(d.total)]),
        }, {
          title: t('expenseReports.sections.weekDistribution'),
          columns: [t('expenseReports.cols.week'), t('expenseReports.cols.count'), t('expenseReports.cols.total')],
          rows: data.weekOfMonthDistribution.map((w) => [t('expenseReports.cols.weekOf', { week: w.weekOfMonth }), w.count, fmtTry(w.total)]),
        }, {
          title: t('expenseReports.sections.patterns'),
          columns: [t('expenseReports.cols.pattern'), t('expenseReports.cols.employee'), t('expenseReports.cols.category'), t('expenseReports.cols.count'), t('expenseReports.cols.value')],
          rows: data.patterns.map((p) => [t(`expenseReports.patterns.${p.patternType}`, { defaultValue: p.patternType }),
            orDash(p.userName), orDash(p.categoryName), p.count, orDash(p.value)]),
        }, {
          title: t('expenseReports.sections.twelveMonth'),
          columns: [t('expenseReports.cols.month'), t('expenseReports.cols.total'), t('expenseReports.cols.count')],
          rows: data.twelveMonthCumulative.map((m) => [fmtMonth(m.monthStart, lang), fmtTry(m.total), m.count]),
        }],
        footnotes: [],
      };
      if (data.limitedHistory) view.footnotes.push(t('expenseReports.notes.limitedHistory'));
      return view;
    }

    case 'vendor': {
      const view = {
        kpis: [
          { label: t('expenseReports.kpi.vendors'), value: data.rows.length },
          { label: t('expenseReports.kpi.total'), value: fmtTry(data.rows.reduce((s, r) => s + r.total, 0)) },
        ],
        sections: [{
          title: t('expenseReports.sections.vendors'),
          columns: [t('expenseReports.cols.vendor'), t('expenseReports.cols.total'), t('expenseReports.cols.receipts'), t('expenseReports.cols.distinctUsers')],
          rows: data.rows.map((r) => [r.merchant, fmtTry(r.total), r.receiptCount, r.distinctUsers]),
        }],
        footnotes: [],
      };
      if (data.receiptsWithoutMerchant > 0) view.footnotes.push(t('expenseReports.notes.merchantMissing', { count: data.receiptsWithoutMerchant }));
      if (data.coverageStart) view.footnotes.push(t('expenseReports.notes.coverageStart', { date: fmtDate(data.coverageStart, lang) }));
      return view;
    }

    case 'comparison':
    default: {
      const view = {
        kpis: [
          { label: t('expenseReports.kpi.comparePeriod'), value: `${fmtDate(data.compareFrom, lang)} – ${fmtDate(data.compareTo, lang)}` },
        ],
        sections: [{
          title: t(`expenseReports.dimensions.${data.dimension}`, { defaultValue: data.dimension }),
          columns: [t('expenseReports.cols.key'), t('expenseReports.cols.current'), t('expenseReports.cols.compare'),
            t('expenseReports.cols.delta'), t('expenseReports.cols.deltaPct'), t('expenseReports.cols.currentCount'), t('expenseReports.cols.compareCount')],
          rows: data.rows.map((r) => [r.key === 'total' ? t('expenseReports.dimensions.total') : r.key,
            fmtTry(r.current), fmtTry(r.compare), fmtTry(r.delta), pct(r.deltaPct), r.currentCount, r.compareCount]),
        }],
        footnotes: [],
      };
      const fx = (data.nonTryCount || 0) + (data.compareNonTryCount || 0);
      if (fx > 0) view.footnotes.push(t('expenseDashboard.fxExcluded', { count: fx }));
      return view;
    }
  }
}
